import type { MarketMatch, MarketsResponse } from '@brochure/shared'

export type Wallet = MarketsResponse['guest']

// Un solo contatore per tutte le richieste: l'ordine di partenza vale fra negozi diversi.
let requestSeq = 0
export const nextSeq = () => ++requestSeq

/**
 * L'ultimo valore noto per chiave, qualunque pagina l'abbia letto. Le pagine Stockbet già viste ripartono dalla loro
 * copia in cache (stale-while-revalidate): leggendo da lì saldo e risultati, cambiando pagina o gruppo di mercati si
 * rivedevano per un attimo i valori vecchi. Qui vince la risposta della richiesta partita per ultima (`nextSeq` alla
 * partenza), qualunque sia l'ordine d'arrivo: una risposta lenta non riporta indietro niente. Finché il valore non
 * cambia `get` restituisce lo stesso oggetto e `getVersion` lo stesso numero (snapshot stabili per React).
 */
export function createLatestStore<V>() {
  const entries = new Map<string, { seq: number; value: V }>()
  const listeners = new Set<() => void>()
  let version = 0
  return {
    nextSeq,
    /** `undefined`: nessuna risposta ancora per questa chiave. */
    get: (key: string): V | undefined => entries.get(key)?.value,
    getVersion: () => version,
    offer(key: string, seq: number, value: V) {
      const current = entries.get(key)
      if (current && current.seq >= seq) return
      if (current && JSON.stringify(current.value) === JSON.stringify(value)) {
        entries.set(key, { seq, value: current.value })
        return
      }
      entries.set(key, { seq, value })
      version++
      for (const listener of listeners) listener()
    },
    subscribe(listener: () => void) {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
  }
}

/** Il saldo dell'ospite: `null` se chi guarda non è un ospite del torneo. */
export const createWalletStore = () => createLatestStore<Wallet>()
export const walletStore = createWalletStore()
export const walletKey = (tournamentId: string, guestToken: string | undefined) => `${tournamentId}|${guestToken ?? ''}`

/** Lo stato di ogni partita (punteggio, stato, 1-2, puntate) come lo vede l'ospite: palinsesto e dettaglio. */
export const matchStore = createLatestStore<MarketMatch>()
export const matchKey = (tournamentId: string, guestToken: string | undefined, matchId: string) => `${walletKey(tournamentId, guestToken)}|${matchId}`
